import { useAppSelector } from "@/redux/hooks";
import BackControls from "../components/BackControls";
import confetti from "canvas-confetti";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import type { College, Department, Track } from "../data/sharedTypes";
import { getCollegeNameKey } from "../data/sharedTypes";
import type { UniversityId } from "../data/universityRegistry";
import { getCollegeMinRate, getDepartmentThreshold, getDisplayedMinRate, getDisplayedMinRateLabelKey, getResultsFailureLines, getRequiredFields, getTrackMinHighSchool, getUniversityInfo, isFlatStructure } from "../data/universityRegistry";
import type { Scores } from "../data/sharedTypes";
import { getQualifiedCollegesWithDepartments, meetsMinHighSchool, meetsMinEnglish } from "../utils/calculateEquivalentRate";
import StepHeader from "../StepHeader";

const fieldLabels: Record<string, string> = {
    high_school: "dream_university_high_school",
    english: "dream_university_english",
    math: "dream_university_math",
    arabic: "dream_university_arabic",
    chemistry: "dream_university_chemistry",
};

const fieldWeightKeys: Record<string, string> = {
    high_school: "highSchoolWeight",
    english: "englishWeight",
    math: "mathWeight",
    arabic: "arabicWeight",
    chemistry: "chemistryWeight",
};

interface Props {
    universityId: UniversityId;
    college: College;
    selectedDepartment: Department | null;
    selectedTrack?: Track | null;
    scores: Scores;
    equivalentRate: number;
    onBack: () => void;
    onShowQualified: () => void;
}

export default function ResultsView({
    universityId,
    college,
    selectedDepartment,
    selectedTrack,
    scores,
    equivalentRate,
    onBack,
    onShowQualified,
}: Props) {
    const { t } = useTranslation();
    const { locale } = useAppSelector((state) => state);
    // Per-section gating threshold. Each section's data module decides what
    // value to compare the student's calculated rate against. The registry
    // is a pure dispatcher.
    const minRate = selectedDepartment
        ? getDepartmentThreshold(universityId, selectedDepartment)
        : getCollegeMinRate(universityId, college.key);
    // Display value can diverge from the qualification gate (e.g. AASU surfaces
    // last year's lowest-accepted HS % per specialty+track while still gating
    // on the apply-floor). The dispatch lives in the registry so per-university
    // logic stays isolated in its own data module.
    const displayedMinRate = getDisplayedMinRate(universityId, selectedDepartment, selectedTrack, college.weights, minRate);
    const passesMinHighSchool = meetsMinHighSchool(universityId, college.weights, scores);
    const passesMinEnglish = meetsMinEnglish(universityId, college.weights, scores);
    const isHigher = passesMinHighSchool && passesMinEnglish && equivalentRate >= minRate;
    const qualified = getQualifiedCollegesWithDepartments(universityId, scores, undefined, selectedTrack);
    const isKu = universityId === "kuwait_university";
    const trackScoped = isKu && selectedTrack
        ? qualified.filter((item) => {
              if (item.college.key === "education_scientific") return selectedTrack === "scientific";
              if (item.college.key === "education_literary") return selectedTrack === "literary";
              return true;
          })
        : qualified;
    const educationKeys = new Set(["education_scientific", "education_literary"]);
    const isKuEducation = isKu && educationKeys.has(college.key);
    const effectiveQualified = trackScoped.filter((item) =>
        isKuEducation
            ? educationKeys.has(item.college.key)
            : item.college.key === college.key,
    );
    // "Opportunities" excludes the specialty the user just calculated for —
    // showing it back to them was confusing. Match by code so departmentKey === null
    // entries (single-specialty colleges like Medicine) still get filtered.
    const qualifiedCount = effectiveQualified.reduce(
        (sum, item) =>
            sum +
            item.departments.filter(
                ({ department }) => department.code !== selectedDepartment?.code,
            ).length,
        0,
    );
    const requiredFields = getRequiredFields(universityId, college.weights);
    const universityInfo = getUniversityInfo(universityId);
    const trackMinHs = getTrackMinHighSchool(universityId, selectedTrack);
    const isFlat = isFlatStructure(universityId);
    const hasFirecConfetti = useRef(false);

    useEffect(() => {
        if (isHigher && !hasFirecConfetti.current) {
            hasFirecConfetti.current = true;

            const defaults = { startVelocity: 30, spread: 360, ticks: 80, zIndex: 100 };

            confetti({ ...defaults, particleCount: 50, origin: { x: 0.1, y: 0.6 } });
            confetti({ ...defaults, particleCount: 50, origin: { x: 0.9, y: 0.6 } });

            setTimeout(() => {
                confetti({ ...defaults, particleCount: 30, origin: { x: 0.3, y: 0.5 } });
                confetti({ ...defaults, particleCount: 30, origin: { x: 0.7, y: 0.5 } });
            }, 250);

            setTimeout(() => {
                confetti({
                    particleCount: 80,
                    spread: 100,
                    origin: { x: 0.5, y: 0.4 },
                    zIndex: 100,
                });
            }, 500);
        }
    }, [isHigher]);

    const getWeight = (field: string): number => {
        const key = fieldWeightKeys[field];
        return (college.weights as unknown as Record<string, number>)[key] || 0;
    };

    const getScoreValue = (field: string): number => {
        return (scores as unknown as Record<string, number | undefined>)[field] || 0;
    };

    return (
        <div className="flex flex-col gap-y-6">
            <BackControls onBack={onBack} />

            {/* Header */}
            <StepHeader
                title={college.weights.admissionType === "high_school"
                    ? t("dream_university_your_hs_rate")
                    : t("dream_university_your_equivalent_rate")}
                subtitle={`${college.emoji} ${t(getCollegeNameKey(college))}`}
            />

            {/* Selection trail */}
            <div className="flex flex-wrap items-center justify-center gap-2">
                {universityInfo && (
                    <span className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full bg-gray-100 text-gray-700">
                        <span>{universityInfo.emoji}</span>
                        {t(universityInfo.nameKey)}
                    </span>
                )}
                <span className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full bg-theme-50 text-theme-700 ring-1 ring-theme-200">
                    <span>{college.emoji}</span>
                    {t(getCollegeNameKey(college))}
                </span>
                {selectedTrack && trackMinHs !== null && (
                    <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full ${
                        selectedTrack === "scientific"
                            ? "bg-blue-50 text-blue-700 ring-1 ring-blue-200"
                            : "bg-purple-50 text-purple-700 ring-1 ring-purple-200"
                    }`}>
                        {t(`dream_university_track_${selectedTrack}`)} · {t("dream_university_track_min_hs", { percent: trackMinHs })}
                    </span>
                )}
                {selectedDepartment?.departmentKey && (
                    <span className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full bg-amber-50 text-amber-700 ring-1 ring-amber-200">
                        {universityId === "internal_missions"
                            ? t(`dream_university_institution_${selectedDepartment.departmentKey}`)
                            : isFlat
                                ? t(`dream_university_country_${selectedDepartment.departmentKey}`)
                                : t(`dream_university_dept_${selectedDepartment.departmentKey}`)}
                    </span>
                )}
            </div>

            {/* Rate Result */}
            {(() => {
                const isKuHsFailure = trackMinHs !== null && !isHigher && passesMinEnglish && minRate > 0;
                return (
                    <div
                        className={`rounded-2xl p-6 text-center ${
                            isHigher ? "bg-theme-100" : "bg-red-100"
                        }`}
                    >
                        <p className="text-5xl font-extrabold mb-3" style={{ color: isHigher ? "#16A18A" : "#DC2626" }}>
                            {equivalentRate}
                        </p>
                        {isKuHsFailure ? (
                            <div className="text-sm text-red-600 space-y-1">
                                {getResultsFailureLines(universityId, college, selectedDepartment, selectedTrack, trackMinHs, minRate).map((line, i) => (
                                    <p key={i}>{t(line.key, line.params)}</p>
                                ))}
                            </div>
                        ) : (
                            <p className={`text-sm ${isHigher ? "text-theme-700" : "text-red-600"}`}>
                                {isHigher
                                    ? (college.weights.admissionType === "high_school"
                                        ? t("dream_university_hs_higher_than_required")
                                        : t("dream_university_higher_than_required"))
                                    : !passesMinHighSchool
                                      ? t("dream_university_hs_below_minimum", { entered: scores.high_school, required: college.weights.minHighSchool })
                                      : !passesMinEnglish
                                        ? t("dream_university_eng_below_minimum", { entered: scores.english || 0, required: college.weights.minEnglish })
                                        : (college.weights.admissionType === "high_school"
                                            ? t("dream_university_hs_lower_than_required")
                                            : t("dream_university_lower_than_required"))}
                            </p>
                        )}
                        {!isKuHsFailure && displayedMinRate > 0 && (
                            universityId === "abdullah_al_salem" && college.key === "medicine_health" && isHigher ? (
                                <>
                                    <p className="text-sm font-medium text-theme-700 mt-1">
                                        {t("dream_university_aasu_can_compete")}
                                    </p>
                                    <p className="text-sm text-gray-600 mt-1">
                                        {t(getDisplayedMinRateLabelKey(universityId, college.weights))}{" "}
                                        <strong>{displayedMinRate}</strong>
                                    </p>
                                </>
                            ) : (
                                <p className="text-sm text-gray-600 mt-1">
                                    {t(getDisplayedMinRateLabelKey(universityId, college.weights))}{" "}
                                    <strong>{displayedMinRate}</strong>
                                </p>
                            )
                        )}
                    </div>
                );
            })()}

            {/* Score Breakdown */}
            <div className="space-y-3">
                {requiredFields.map((field) => (
                    <div
                        key={field}
                        className="flex items-center justify-between bg-gray-50 rounded-xl p-4"
                    >
                        <div>
                            <p className="text-xs text-gray-500">
                                {t(fieldLabels[field])}
                            </p>
                            <p className="text-lg font-bold text-gray-900">
                                {getScoreValue(field)}
                            </p>
                        </div>
                        <span className="text-xs text-gray-500">
                            {t("dream_university_weight_percentage")}{" "}
                            {getWeight(field)}%
                        </span>
                    </div>
                ))}
            </div>

            {/* Qualified Colleges Button */}
            {qualifiedCount > 0 && (
                <button
                    onClick={onShowQualified}
                    className="w-full py-3.5 bg-theme-700 text-white font-semibold rounded-xl hover:bg-theme-800 transition-colors"
                >
                    {qualifiedCount} {t("dream_university_other_colleges_eligible")}
                </button>
            )}
        </div>
    );
}
